import { createSelector } from "reselect";
import { sortByStartPen } from "lib/auctionPens";
import { PenTypes } from "constants/auctionPens";
import { EMPTY_ARRAY } from "lib";
import { getAuctionPens } from "../root";
import { selectSaleLotIdsByAuctionPenIdLookup } from "../indexes";
import { selectSaleLotIdsByMarkOrder } from "./index";

interface InlineAuctionPen {
  id: string;
  penType: PenTypes;
}

// Finds sale lots ordered by pen order, then by mark order within.
// Returns a lookup of sale lot id -> sale lot id.
const selectNextSaleLotIdBySaleLotIdLookup = createSelector(
  [
    getAuctionPens,
    selectSaleLotIdsByAuctionPenIdLookup,
    selectSaleLotIdsByMarkOrder,
  ],
  (
    auctionPens: Record<string, InlineAuctionPen>,
    saleLotIdsByAuctionPenIdLookup: Record<string, string[]>,
    saleLotIdsByMarkOrder: string[],
  ) => {
    const sortedSaleLotIds = sortByStartPen(
      Object.values(auctionPens).filter(
        auctionPen => auctionPen.penType === PenTypes.SELLING,
      ),
    )
      .map(auctionPen => {
        const saleLotIds =
          saleLotIdsByAuctionPenIdLookup[auctionPen.id] || EMPTY_ARRAY;

        // Return the filtered results of the sorted-by-mark set (ie, the subset
        // of sorted lots, that are in this pen)
        return saleLotIdsByMarkOrder.filter(saleLotId =>
          saleLotIds.includes(saleLotId),
        );
      })
      .reduce((acc, saleLotIds) => acc.concat(saleLotIds), []);

    const saleLotIdsLookup = sortedSaleLotIds.reduce(
      (acc, saleLotId, index) => {
        if (index <= sortedSaleLotIds.length - 1) {
          acc[saleLotId] = sortedSaleLotIds[index + 1];
        }
        return acc;
      },
      {},
    );
    return saleLotIdsLookup;
  },
);

export const getNextSaleLotIdBySaleLotId = (saleLotId: string) => state =>
  selectNextSaleLotIdBySaleLotIdLookup(state)[saleLotId] || null;
